import { Fragment, useMemo } from 'react';
import { Boxes, Cpu } from 'lucide-react';
import type { LaunchMentionProviderProps } from '@reflex/plugin-api';
import { DEVBOX_CAPABILITIES, SANDBOX_RESOURCE_SIZE_OPTIONS } from '@reflex/shared';
import { CommandGroup } from '@reflex/ui/components/ui/command';
import { MentionCommandItem } from '@reflex/ui/components/shared/MentionCommandList';

/**
 * Launch `@`-mention provider for the devbox sandbox. Two kinds of quick
 * actions, both first-class launch config (like personas) rather than plugin
 * attachments:
 *
 *   - Each devbox capability from {@link DEVBOX_CAPABILITIES} is its own row, so
 *     it can be referenced directly (e.g. `@dockerd` or `@sandbox:dockerd`);
 *     selecting one enables that single capability.
 *   - Each named devbox size from {@link SANDBOX_RESOURCE_SIZE_OPTIONS} sets the
 *     run's resource size.
 *
 * Sandbox config is a core concern (not plugin-owned), so this provider lives
 * in the host and is rendered directly by {@link LaunchMentionMenu}. The menu
 * disables cmdk filtering (each provider owns its own ranking), so this filters
 * its rows against `query` itself.
 */
export function SandboxMentionProvider({
  query,
  onApply,
  groupHeading,
}: LaunchMentionProviderProps) {
  const q = query.trim().toLowerCase();

  const capabilityMatches = useMemo(
    () =>
      DEVBOX_CAPABILITIES.filter((capability) => {
        if (q.length === 0) return true;
        // Match the capability's own name/id and the category keyword, so both
        // "@dockerd" and "@sandbox" surface it.
        const haystack = `${capability.label} ${capability.id} ${capability.description} sandbox`;
        return haystack.toLowerCase().includes(q);
      }),
    [q],
  );

  const sizeMatches = useMemo(
    () =>
      SANDBOX_RESOURCE_SIZE_OPTIONS.filter((size) => {
        if (q.length === 0) return true;
        const haystack =
          `${size.label} ${size.value} ${size.description} sandbox size`.toLowerCase();
        return haystack.includes(q);
      }),
    [q],
  );

  if (capabilityMatches.length === 0 && sizeMatches.length === 0) return null;

  const capabilityRows = capabilityMatches.map((capability) => (
    <MentionCommandItem
      key={`sandbox-capability-${capability.id}`}
      value={`sandbox-capability:${capability.id}`}
      keywords={[capability.label, capability.id, 'capability', 'sandbox']}
      testId={`sandbox-mention-capability-${capability.id}`}
      onSelect={() => onApply({ kind: 'set-sandbox', capabilityId: capability.id })}
      icon={<Boxes />}
      name={
        <span className="flex min-w-0 items-baseline gap-1.5">
          <span className="truncate">{capability.label}</span>
          <span className="truncate text-2xs text-muted-foreground">{capability.description}</span>
        </span>
      }
    />
  ));

  const sizeRows = sizeMatches.map((size) => (
    <MentionCommandItem
      key={`sandbox-size-${size.value}`}
      value={`sandbox-size:${size.value}`}
      keywords={[size.label, size.value, 'sandbox', 'size']}
      testId={`sandbox-mention-size-${size.value}`}
      onSelect={() => onApply({ kind: 'set-sandbox', resourceSize: size.value })}
      icon={<Cpu />}
      name={
        <span className="flex min-w-0 items-baseline gap-1.5">
          <span className="truncate">{size.label}</span>
          <span className="truncate text-2xs text-muted-foreground">{size.description}</span>
        </span>
      }
    />
  ));

  // Flattened root search hands us a single heading for all of this provider's
  // rows; the scoped (`@sandbox:`) view has none, so we label the two subgroups
  // ourselves to keep capabilities and sizes legible.
  if (groupHeading) {
    return (
      <CommandGroup heading={groupHeading}>
        {capabilityRows}
        {sizeRows}
      </CommandGroup>
    );
  }

  return (
    <Fragment>
      {capabilityRows.length > 0 ? (
        <CommandGroup heading="Capabilities">{capabilityRows}</CommandGroup>
      ) : null}
      {sizeRows.length > 0 ? <CommandGroup heading="Sizes">{sizeRows}</CommandGroup> : null}
    </Fragment>
  );
}
